<template>
  <div class="compare-test-result-container">
    <div class="page-header">
      <h2>对比测试结果</h2>
      <p class="subtitle">订单编号: {{ orderInfo.originalOrderSn }}</p>
    </div>

    <el-card class="order-info-card" shadow="hover">
      <div slot="header" class="card-header">
        <span><i class="el-icon-info"></i> 订单信息</span>
      </div>
      <el-descriptions :column="2" border>
        <el-descriptions-item label="项目名称">{{ orderInfo.projectName }}</el-descriptions-item>
        <el-descriptions-item label="客户名称">{{ orderInfo.customerName }}</el-descriptions-item>
        <el-descriptions-item label="样品数量">{{ orderInfo.testCount }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ orderInfo.createTime }}</el-descriptions-item>
      </el-descriptions>
    </el-card>

    <el-card class="test-requirements-card" shadow="hover">
      <div slot="header" class="card-header">
        <span><i class="el-icon-document"></i> 测试要求</span>
      </div>
      <div class="test-requirements-content">
        <div v-if="orderInfo.testFileList && orderInfo.testFileList.length > 0" class="attachment-list">
          <div v-for="(file, index) in orderInfo.testFileList" :key="index" class="attachment-item">
            <i class="el-icon-document"></i>
            <a :href="getFileUrl(file)" target="_blank" class="attachment-link">{{ file.fileName }}</a>
            <span class="attachment-size">({{ formatFileSize(file.fileSize) }})</span>
            <div class="attachment-actions">
              <el-button type="text" size="mini" @click="previewFile(file)">预览</el-button>
              <el-button type="text" size="mini" @click="downloadFile(file)">下载</el-button>
            </div>
          </div>
        </div>
        <div v-else class="no-attachment">暂无测试要求</div>
      </div>
    </el-card>

    <el-card class="dispute-info-card" shadow="hover">
      <div slot="header" class="card-header">
        <span><i class="el-icon-warning"></i> 争议信息</span>
      </div>
      <div class="dispute-info-content">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="争议说明">
            <div class="dispute-description">
              <pre>{{ orderInfo.orderDisputeRemark || '暂无争议说明' }}</pre>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="争议附件">
            <div class="dispute-attachments">
              <div v-if="orderInfo.disputeFileList && orderInfo.disputeFileList.length > 0" class="attachment-list">
                <div v-for="(file, index) in orderInfo.disputeFileList" :key="index" class="attachment-item">
                  <i class="el-icon-document"></i>
                  <a :href="getFileUrl(file)" target="_blank" class="attachment-link">{{ file.fileName }}</a>
                  <span class="attachment-size">({{ formatFileSize(file.fileSize) }})</span>
                  <div class="attachment-actions">
                    <el-button type="text" size="mini" @click="previewFile(file)">预览</el-button>
                    <el-button type="text" size="mini" @click="downloadFile(file)">下载</el-button>
                  </div>
                </div>
              </div>
              <div v-else class="no-attachment">暂无争议附件</div>
            </div>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-card>

    <el-card class="test-results-card" shadow="hover">
      <div slot="header" class="card-header">
        <span><i class="el-icon-data-analysis"></i> 测试结果对比</span>
      </div>
      <div class="test-results-content">
        <el-row :gutter="20">
          <el-col :span="12">
            <div class="result-section">
              <h3><i class="el-icon-s-shop"></i> 原商家测试结果</h3>
              <el-descriptions :column="1" border>
                <el-descriptions-item label="商家名称">{{ orderInfo.originalMerchantName }}</el-descriptions-item>
                <el-descriptions-item label="订单编号">{{ orderInfo.originalOrderSn }}</el-descriptions-item>
                <el-descriptions-item label="测试仪器">{{ orderInfo.originalMerchantDevice }}</el-descriptions-item>
                <el-descriptions-item label="订单金额">¥{{ orderInfo.originalOrderPrice || '0.00' }}</el-descriptions-item>
                <el-descriptions-item label="测试结果">
                  <div class="result-content">
                    <div v-if="orderInfo.originalResultFileList && orderInfo.originalResultFileList.length > 0" class="attachment-list">
                      <div class="attachment-header">
                        <span class="attachment-count">共 {{ orderInfo.originalResultFileList.length }} 个文件</span>
                        <el-button type="text" size="mini" @click="toggleAttachmentList('original')">
                          {{ isOriginalAttachmentCollapsed ? '展开' : '收起' }}
                          <i :class="isOriginalAttachmentCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                        </el-button>
                      </div>
                      <div v-show="!isOriginalAttachmentCollapsed" class="attachment-items">
                        <div v-for="(file, index) in orderInfo.originalResultFileList" :key="index" class="attachment-item">
                          <i class="el-icon-document"></i>
                          <a :href="getFileUrl(file)" target="_blank" class="attachment-link">{{ file.fileName }}</a>
                          <span class="attachment-size">({{ formatFileSize(file.fileSize) }})</span>
                          <div class="attachment-actions">
                            <el-button type="text" size="mini" @click="previewFile(file)">预览</el-button>
                            <el-button type="text" size="mini" @click="downloadFile(file)">下载</el-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="no-attachment">暂无测试结果</div>
                  </div>
                </el-descriptions-item>
                <el-descriptions-item label="测试说明">
                  <div class="result-content">
                    <pre>{{ orderInfo.originalResultRemark || '暂无测试说明' }}</pre>
                  </div>
                </el-descriptions-item>
              </el-descriptions>
            </div>
          </el-col>
          <el-col :span="12">
            <div class="result-section">
              <h3><i class="el-icon-s-shop"></i> 转寄商家测试结果</h3>
              <el-descriptions :column="1" border>
                <el-descriptions-item label="商家名称">{{ orderInfo.forwardMerchantName }}</el-descriptions-item>
                <el-descriptions-item label="订单编号">{{ orderInfo.forwardOrderSn }}</el-descriptions-item>
                <el-descriptions-item label="测试仪器">{{ orderInfo.forwardMerchantDevice }}</el-descriptions-item>
                <el-descriptions-item label="订单金额">¥{{ orderInfo.forwardOrderPrice || '0.00' }}</el-descriptions-item>
                <el-descriptions-item label="测试结果">
                  <div class="result-content">
                    <div v-if="orderInfo.forwardResultFileList && orderInfo.forwardResultFileList.length > 0" class="attachment-list">
                      <div class="attachment-header">
                        <span class="attachment-count">共 {{ orderInfo.forwardResultFileList.length }} 个文件</span>
                        <el-button type="text" size="mini" @click="toggleAttachmentList('forward')">
                          {{ isForwardAttachmentCollapsed ? '展开' : '收起' }}
                          <i :class="isForwardAttachmentCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
                        </el-button>
                      </div>
                      <div v-show="!isForwardAttachmentCollapsed" class="attachment-items">
                        <div v-for="(file, index) in orderInfo.forwardResultFileList" :key="index" class="attachment-item">
                          <i class="el-icon-document"></i>
                          <a :href="getFileUrl(file)" target="_blank" class="attachment-link">{{ file.fileName }}</a>
                          <span class="attachment-size">({{ formatFileSize(file.fileSize) }})</span>
                          <div class="attachment-actions">
                            <el-button type="text" size="mini" @click="previewFile(file)">预览</el-button>
                            <el-button type="text" size="mini" @click="downloadFile(file)">下载</el-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="no-attachment">暂无测试结果</div>
                  </div>
                </el-descriptions-item>
                <el-descriptions-item label="测试说明">
                  <div class="result-content">
                    <pre>{{ orderInfo.forwardResultRemark || '暂无测试说明' }}</pre>
                  </div>
                </el-descriptions-item>
              </el-descriptions>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-card>

    <el-card class="process-result-card" shadow="hover">
      <div slot="header" class="card-header">
        <span><i class="el-icon-s-check"></i> 处理结果</span>
      </div>
      <div class="process-result-content">
        <el-radio-group v-model="processResult" class="radio-group">
          <el-radio label="original" class="radio-item">
            <span class="radio-label">认可原商家测试结果</span>
          </el-radio>
          <el-radio label="forward" class="radio-item">
            <span class="radio-label">认可转寄商家测试结果</span>
          </el-radio>
          <el-radio label="both" class="radio-item">
            <span class="radio-label">都认可</span>
          </el-radio>
        </el-radio-group>
        <div class="submit-button">
          <el-button type="primary" size="medium" @click="handleSubmit">提交</el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import adminApi from '@/api/adminApi'

export default {
  name: 'CompareTestResult',
  data() {
    return {
      orderId: null,
      orderInfo: {},
      processResult: 'original',
      isOriginalAttachmentCollapsed: false,
      isForwardAttachmentCollapsed: false
    }
  },
  created() {
    this.orderId = this.$route.params.id
    this.fetchOrderInfo()
  },
  methods: {
    async fetchOrderInfo() {
      try {
        const response = await adminApi.getCompareTestResult({ id: this.orderId })
        console.log('接口返回数据:', response)
        if (response) {
          this.orderInfo = response
        } else {
          this.$message.error('获取订单信息失败')
        }
      } catch (error) {
        console.error('获取订单信息失败:', error)
        this.$message.error('获取订单信息失败')
      }
    },
    async handleSubmit() {
      try {
        const params = {
          orderId: this.orderId,
          disputeResult: this.processResult
        }

        // 根据选择的处理结果传递对应的订单编号
        if (this.processResult === 'original') {
          params.orderSn = this.orderInfo.originalOrderSn
        } else if (this.processResult === 'forward') {
          params.orderSn = this.orderInfo.forwardOrderSn
        } else if (this.processResult === 'both') {
          // 如果选择都认可，默认使用原商家订单编号
          params.orderSn = this.orderInfo.originalOrderSn
        }

        console.log('提交参数:', params)
        const response = await adminApi.submitCompareResult(params)
        console.log('提交结果:', response)
        if (response) {
          this.$message.success('提交成功')
          this.$router.push('/admin/orders/pending')
        } else {
          this.$message.error('提交失败')
        }
      } catch (error) {
        console.error('提交失败:', error)
        this.$message.error('提交失败')
      }
    },
    formatFileSize(size) {
      if (!size) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let index = 0
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024
        index++
      }
      return `${size.toFixed(2)} ${units[index]}`
    },
    getFileUrl(file) {
      if (!file || !file.filePath) return '#'
      return `${adminApi.ApiUrl}/app/v1/file/download?filePath=${encodeURIComponent(file.filePath)}`
    },
    previewFile(file) {
      if (!file) {
        this.$message.warning('文件信息不完整')
        return
      }
      
      // 调用统一的预览方法
      adminApi.previewFile(file, this.$message)
        .catch(error => {
          console.error('文件预览失败:', error);
        });
    },
    downloadFile(file) {
      if (!file) {
        this.$message.warning('文件信息不完整')
        return
      }
      
      // 调用统一的下载方法
      adminApi.downloadFileUnified(file, this.$message)
        .catch(error => {
          console.error('文件下载失败:', error);
        });
    },
    getFileTypeFromName(fileName) {
      if (!fileName) return 0
      const extension = fileName.split('.').pop().toLowerCase()
      
      // 根据扩展名判断文件类型
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension)) return 1 // 图片
      if (['pdf'].includes(extension)) return 2 // PDF
      if (['doc', 'docx'].includes(extension)) return 3 // Word
      if (['xls', 'xlsx'].includes(extension)) return 4 // Excel
      if (['ppt', 'pptx'].includes(extension)) return 5 // PowerPoint
      if (['txt'].includes(extension)) return 6 // 文本
      
      return 0 // 其他
    },
    isImageFile(fileType) {
      return fileType === 1
    },
    isPdfFile(fileType) {
      return fileType === 2
    },
    toggleAttachmentList(type) {
      if (type === 'original') {
        this.isOriginalAttachmentCollapsed = !this.isOriginalAttachmentCollapsed
      } else if (type === 'forward') {
        this.isForwardAttachmentCollapsed = !this.isForwardAttachmentCollapsed
      }
    }
  }
}
</script>

<style scoped>
.compare-test-result-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.page-header h2 {
  margin: 0;
  font-size: 22px;
  color: #303133;
}

.page-header .subtitle {
  margin: 5px 0 0;
  font-size: 14px;
  color: #909399;
}

.order-info-card,
.test-requirements-card,
.dispute-info-card,
.test-results-card,
.process-result-card {
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fb;
  padding: 12px 20px;
}

.card-header span {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
  display: flex;
  align-items: center;
}

.card-header i {
  margin-right: 8px;
  font-size: 18px;
}

.test-requirements-content,
.result-content,
.dispute-description,
.dispute-attachments {
  padding: 15px;
  background-color: #ffffff;
  border-radius: 4px;
  min-height: 100px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.result-section {
  margin-bottom: 20px;
}

.result-section h3 {
  margin-bottom: 15px;
  color: #303133;
  font-size: 16px;
  display: flex;
  align-items: center;
}

.result-section h3 i {
  margin-right: 8px;
  color: #409EFF;
}

.process-result-content {
  padding: 20px;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.radio-item {
  padding: 12px 20px;
  border-radius: 4px;
  transition: all 0.3s;
  border: 1px solid #dcdfe6;
  margin: 0;
}

.radio-item:hover {
  background-color: #f5f7fa;
  border-color: #c0c4cc;
}

.radio-item.is-checked {
  background-color: #ecf5ff;
  border-color: #409EFF;
}

.radio-label {
  font-size: 14px;
  color: #606266;
}

.radio-item.is-checked .radio-label {
  color: #409EFF;
  font-weight: 500;
}

/* 隐藏默认的单选框样式 */
.radio-item .el-radio__input {
  display: none;
}

.radio-item .el-radio__label {
  padding-left: 0;
}

.submit-button {
  margin-top: 30px;
  text-align: center;
}

.submit-button .el-button {
  padding: 12px 30px;
  font-size: 16px;
}

pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  line-height: 1.5;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background-color: #f8f9fb;
  border-radius: 4px;
  transition: all 0.3s;
  border: 1px solid #ebeef5;
}

.attachment-item:hover {
  background-color: #ecf5ff;
  border-color: #d9ecff;
  transform: translateY(-2px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.attachment-link {
  color: #409EFF;
  text-decoration: none;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.attachment-link:hover {
  text-decoration: underline;
}

.attachment-size {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}

.attachment-actions {
  display: flex;
  gap: 5px;
  white-space: nowrap;
}

.attachment-actions .el-button {
  padding: 4px 8px;
}

.no-attachment {
  color: #909399;
  text-align: center;
  padding: 30px 0;
  font-style: italic;
}

.attachment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 10px;
}

.attachment-count {
  color: #606266;
  font-size: 14px;
}

.attachment-items {
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

.attachment-items::-webkit-scrollbar {
  width: 6px;
}

.attachment-items::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 3px;
}

.attachment-items::-webkit-scrollbar-track {
  background-color: #f5f7fa;
}

/* 响应式调整 */
@media screen and (max-width: 768px) {
  .el-row {
    flex-direction: column;
  }
  
  .el-col {
    width: 100% !important;
    margin-bottom: 20px;
  }
  
  .attachment-item {
    flex-wrap: wrap;
  }
  
  .attachment-actions {
    width: 100%;
    justify-content: flex-end;
    margin-top: 5px;
  }
}
</style> 